import React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./BarChart.scss";

export interface BarChartDataItem {
  name: string;
  [key: string]: any;
}

export interface BarConfig {
  dataKey: string;
  name: string;
  color: string;
  gradientId?: string;
  animationDelay?: number;
}

interface BarChartProps {
  data: BarChartDataItem[];
  bars: BarConfig[];
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  animationDuration?: number;
  className?: string;
  margin?: { top?: number; right?: number; left?: number; bottom?: number };
  xAxisAngle?: number;
  layout?: "horizontal" | "vertical";
  item?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  bars,
  height = 300,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  animationDuration = 1500,
  className = "",
  margin = { top: 20, right: 30, left: 20, bottom: 5 },
  xAxisAngle = 0,
  layout = "horizontal",
  item = true,
}) => {
  const renderCustomTooltip = (props: any) => {
    if (!props.active || !props.payload || !props.label) return null;

    // Data item ni olish (students va completed uchun)
    const dataItem = props.payload[0]?.payload;

    // Agar students va completed bo'lsa, faqat ularni ko'rsatish
    const hasStudentsAndCompleted =
      dataItem &&
      (dataItem.students !== undefined || dataItem.completed !== undefined);

    return (
      <div className="bar-chart__tooltip">
        <div className="bar-chart__tooltip-label">{props.label}</div>
        <div className="bar-chart__tooltip-content">
          {/* item prop true bo'lsa, oddiy payload itemlarini ko'rsatish */}
          {item &&
            props.payload.map((entry: any, index: number) => (
              <div key={index} className="bar-chart__tooltip-item">
                <div
                  className="bar-chart__tooltip-color"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="bar-chart__tooltip-name">{entry.name}:</span>
                <span className="bar-chart__tooltip-value">{entry.value}</span>
              </div>
            ))}
          {/* Jami talabalar soni va qatnashgan talabalar soni */}
          {hasStudentsAndCompleted && (
            <>
              {dataItem.students !== undefined && (
                <div className="bar-chart__tooltip-item">
                  <div
                    className="bar-chart__tooltip-color"
                    style={{ backgroundColor: "transparent" }}
                  />
                  <span className="bar-chart__tooltip-name">
                    Jami talabalar:
                  </span>
                  <span className="bar-chart__tooltip-value">
                    {dataItem.students}
                  </span>
                </div>
              )}
              {dataItem.completed !== undefined && (
                <div className="bar-chart__tooltip-item">
                  <div
                    className="bar-chart__tooltip-color"
                    style={{ backgroundColor: "transparent" }}
                  />
                  <span className="bar-chart__tooltip-name">
                    Qatnashgan talabalar:
                  </span>
                  <span className="bar-chart__tooltip-value">
                    {dataItem.completed}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Y o'qidagi label-larni wrap qilish uchun custom tick render funksiyasi
  const renderYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value;
    const maxWidth = 300; // Maksimal kenglik
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word: string) => {
      const testLine = currentLine + (currentLine ? " " : "") + word;
      // Taxminiy har bir belgi uchun 7px (font-size 12px uchun)
      const testWidth = testLine.length * 7;

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={-10}
          y={0}
          dy={lines.length > 1 ? -((lines.length - 1) * 6) : 0}
          textAnchor="end"
          fill="#718096"
          fontSize={12}
          fontWeight={500}
        >
          {lines.map((line, index) => (
            <tspan
              key={index}
              x={-10}
              dy={index === 0 ? 0 : 14}
              textAnchor="end"
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  // Agar angle berilgan bo'lsa, bottom margin ni oshirish
  // Agar vertical layout bo'lsa, left margin ni oshirish (Y o'qi uchun)
  const isVertical = layout === "vertical";
  const adjustedMargin = isVertical
    ? {
        ...margin,
        left: (margin.left || 20) + 150, // Y o'qi uchun ko'proq joy (wrap qilish uchun)
        bottom: margin.bottom || 5,
      }
    : {
        ...margin,
        bottom:
          xAxisAngle !== 0 ? (margin.bottom || 5) + 50 : margin.bottom || 5,
      };

  return (
    <div className={`bar-chart ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={data}
          margin={adjustedMargin}
          layout={layout}
          barCategoryGap={isVertical ? "15%" : "20%"}
          barSize={isVertical ? 40 : undefined}
        >
          <defs>
            {bars.map((bar, index) => (
              <linearGradient
                key={index}
                id={bar.gradientId || `barGradient${index}`}
                x1={isVertical ? "0" : "0"}
                y1={isVertical ? "0" : "0"}
                x2={isVertical ? "1" : "0"}
                y2={isVertical ? "0" : "1"}
              >
                <stop offset="5%" stopColor={bar.color} stopOpacity={0.9} />
                <stop offset="95%" stopColor={bar.color} stopOpacity={0.7} />
              </linearGradient>
            ))}
          </defs>
          {showGrid && <CartesianGrid strokeDasharray="3 3" />}
          {isVertical ? (
            <>
              <XAxis type="number" />
              <YAxis
                type="category"
                dataKey="name"
                tick={renderYAxisTick}
                interval={0}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                angle={xAxisAngle}
                textAnchor={
                  xAxisAngle === -90
                    ? "middle"
                    : xAxisAngle !== 0
                    ? "end"
                    : "middle"
                }
                height={
                  xAxisAngle !== 0 ? (xAxisAngle === -90 ? 100 : 80) : undefined
                }
              />
              <YAxis />
            </>
          )}
          {showTooltip && (
            <Tooltip
              content={renderCustomTooltip}
              animationDuration={300}
              animationEasing="ease-out"
            />
          )}
          {showLegend && <Legend />}
          {bars.map((bar, index) => (
            <Bar
              key={index}
              dataKey={bar.dataKey}
              fill={`url(#${bar.gradientId || `barGradient${index}`})`}
              name={bar.name}
              radius={isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}
              animationBegin={bar.animationDelay || index * 200}
              animationDuration={animationDuration}
              animationEasing="ease-out"
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;
